/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { RainbowProgressBar } from '../../primitives/rainbow-progress-bar/RainbowProgressBar';
import { RovoThinkingIndicator } from '../thinking-indicator/ThinkingIndicator';

import messages from './messages';
import { useProgressFacade } from './useProgressFacade';

// Rotating titles advance every 4s and stop on the last entry.
const ROTATION_INTERVAL_MS = 4000;

const rotatingMessages = defineMessages({
	rovoIsThinking: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.rovo-is-thinking',
		defaultMessage: 'Rovo is thinking',
		description: 'Initial loading message shown while Rovo is starting to generate a response.',
	},
	analyzingContent: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.analyzing-content',
		defaultMessage: 'Analyzing your content',
		description: 'Loading message shown while Rovo is analyzing the content.',
	},
	puttingPiecesTogether: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.putting-pieces-together',
		defaultMessage: 'Putting the pieces together',
		description: 'Loading message shown while Rovo is assembling content.',
	},
	structuringResponse: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.structuring-response',
		defaultMessage: 'Structuring your response',
		description: 'Loading message shown while Rovo is structuring the output.',
	},
	designingResponse: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.designing-response',
		defaultMessage: 'Designing your response',
		description: 'Loading message shown while Rovo is designing the output.',
	},
	addingItAllTogether: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.adding-it-all-together',
		defaultMessage: 'Adding it all together',
		description: 'Loading message shown while Rovo is combining all elements.',
	},
	applyingFinalTouches: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.applying-final-touches',
		defaultMessage: 'Applying the final touches',
		description: 'Loading message shown while Rovo is finalizing.',
	},
	almostDone: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.almost-done',
		defaultMessage: 'Almost done',
		description: 'Loading message shown when Rovo is nearly finished.',
	},
});

/**
 * Cycles through `texts` at a fixed interval, stopping on the last item.
 *
 * When `startedAt` (epoch ms) is provided, the current index is derived from the
 * elapsed time since that shared anchor, so multiple instances driven by the same
 * session show the same message and stay in sync across mount/unmount. When
 * omitted, rotation is anchored to this mount.
 */
function useSequentialText(texts: string[], enabled: boolean, startedAt?: number | null): string {
	const indexFromStart = (): number => {
		if (startedAt == null) {
			return 0;
		}
		const elapsed = Math.max(0, Date.now() - startedAt);
		return Math.min(texts.length - 1, Math.floor(elapsed / ROTATION_INTERVAL_MS));
	};

	const [index, setIndex] = useState(indexFromStart);
	const indexRef = useRef(index);
	indexRef.current = index;
	const textsRef = useRef(texts);
	textsRef.current = texts;

	useEffect(() => {
		if (!enabled || textsRef.current.length <= 1) {
			return;
		}
		if (indexRef.current >= textsRef.current.length - 1) {
			return;
		}
		const interval = setInterval(() => {
			const next = indexRef.current + 1;
			if (next < textsRef.current.length) {
				indexRef.current = next;
				setIndex(next);
			} else {
				clearInterval(interval);
			}
		}, ROTATION_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [enabled, startedAt]);

	return texts[index] ?? texts[texts.length - 1] ?? '';
}

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		width: '100%',
	},

	actions: {
		marginLeft: 'auto',
	},

	indicatorWrapper: {
		flex: '1',
		minWidth: 0,
		overflow: 'hidden',
	},
});

export type RovoThinkingBarStatus = 'analyzing' | 'editing';

export type RovoThinkingBarProps = {
	status?: RovoThinkingBarStatus;
	onStop: () => void;
	onFollow?: () => void;
	useShortMessages?: boolean;
	/**
	 * When true, renders a RainbowProgressBar below the thinking indicator.
	 */
	showProgressBar?: boolean;
	/**
	 * Signals the progress bar to rapidly fill to 100%.
	 * Only relevant when `showProgressBar` is true.
	 */
	isComplete?: boolean;
	/**
	 * Called after the progress bar finish-to-100% animation completes.
	 * Only relevant when `showProgressBar` is true.
	 */
	onCompleteAnimationFinished?: () => void;
	/** When true, derives the indicator title from a rotating list of messages. */
	useRotatingTitle?: boolean;
	/**
	 * Custom rotating messages to display instead of the default set.
	 * Only used when `useRotatingTitle` is true.
	 *
	 * Strings must already be localized by the caller; they are rendered as-is
	 * without going through `react-intl`.
	 */
	customRotatingMessages?: string[];
	/**
	 * Epoch ms (e.g. `Date.now()`) marking when the thinking session started.
	 * When provided, the progress bar and rotating title are anchored to this
	 * shared time so multiple bars driven by the same session stay in sync across
	 * mount/unmount. When omitted, they anchor to this mount.
	 */
	startedAt?: number | null;
	/**
	 * Fixed progress-bar facade target (0-100) for the session. When provided,
	 * replaces the per-mount random target so multiple bars fill to the same value.
	 */
	progressTarget?: number | null;
};

/**
 * Inner content of the Rovo thinking bar — thinking indicator + action buttons.
 *
 * Extracted from ThinkingView in conversation-assistant-chat-prompt-input so it
 * can be reused in contexts outside of the inline chat prompt flow (e.g. BOE
 * whiteboard and database streaming experiences).
 */
export const RovoThinkingBar = ({
	status = 'analyzing',
	onStop,
	onFollow,
	useShortMessages = false,
	showProgressBar = false,
	isComplete,
	onCompleteAnimationFinished,
	useRotatingTitle = false,
	customRotatingMessages,
	startedAt,
	progressTarget,
}: RovoThinkingBarProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const progress = useProgressFacade({
		isComplete,
		onCompleteAnimationFinished,
		startedAt,
		target: progressTarget,
	});

	const staticTitle = formatMessage(
		status === 'editing'
			? useShortMessages
				? messages.editingTextShort
				: messages.editingText
			: useShortMessages
				? messages.thinkingTextShort
				: messages.thinkingText,
	);

	const rotatingEnabled = useRotatingTitle && !useShortMessages;
	const defaultRotatingTitles = [
		formatMessage(rotatingMessages.rovoIsThinking),
		formatMessage(rotatingMessages.analyzingContent),
		formatMessage(rotatingMessages.puttingPiecesTogether),
		formatMessage(rotatingMessages.structuringResponse),
		formatMessage(rotatingMessages.designingResponse),
		formatMessage(rotatingMessages.addingItAllTogether),
		formatMessage(rotatingMessages.applyingFinalTouches),
		formatMessage(rotatingMessages.almostDone),
	];
	const rotatingTitles =
		customRotatingMessages && customRotatingMessages.length > 0
			? customRotatingMessages
			: defaultRotatingTitles;
	const rotatingTitle = useSequentialText(rotatingTitles, rotatingEnabled, startedAt);

	const title = rotatingEnabled ? rotatingTitle : staticTitle;

	const actionsNode = (
		<Inline
			testId="inline-chat__thinking-view"
			alignBlock="center"
			space="space.100"
			xcss={styles.actions}
		>
			{onFollow && (
				<Button appearance="subtle" onClick={onFollow}>
					{formatMessage(messages.followAiMateButton)}
				</Button>
			)}
			<IconButton
				isTooltipDisabled={false}
				shape={'default'}
				onClick={onStop}
				icon={(iconProps) => (
					<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label={''} />
				)}
				label={formatMessage(messages.stopGeneratingButtonLabel)}
			/>
		</Inline>
	);

	return (
		<Box xcss={styles.container}>
			<Box xcss={styles.indicatorWrapper}>
				<RovoThinkingIndicator
					title={title}
					animation
					bottomAdornment={
						showProgressBar ? (
							<RainbowProgressBar
								testId="rovo-thinking-progress-bar"
								progress={progress}
								appearance="static-rainbow"
								label={title}
							/>
						) : undefined
					}
				/>
			</Box>
			{actionsNode}
		</Box>
	);
};
